import { type RefObject, useEffect, useRef } from 'react';

interface UseInViewChangeOptions {
  /**
   * IntersectionObserver threshold, default 0.5. A fractional threshold can
   * never be satisfied by an element taller than the viewport - its
   * intersection ratio tops out below 1 and so never crosses a mid-range
   * threshold, and the callback never fires. Callers tracking an element that
   * may be taller than the viewport (e.g. a long product grid) should pass 0.
   */
  threshold?: number;
  disabled?: boolean;
  /**
   * Re-arms the hook when it changes, so a list whose visible result set has
   * changed (filter, sort, pagination) counts as a fresh impression. Omit it
   * for a list that can never change - that is the original fire-once
   * behaviour.
   */
  signature?: string;
}

export const useInViewChange = (
  ref: RefObject<Element | null>,
  callback: () => void,
  { threshold = 0.5, disabled = false, signature }: UseInViewChangeOptions = {},
) => {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const hasFiredRef = useRef(false);
  const lastSignatureRef = useRef(signature);

  useEffect(() => {
    // Re-arm before the guard below, so a signature change revives a hook that
    // has already fired and disconnected its observer.
    if (lastSignatureRef.current !== signature) {
      lastSignatureRef.current = signature;
      hasFiredRef.current = false;
    }

    const element = ref.current;

    if (disabled || hasFiredRef.current || !element) {
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      hasFiredRef.current = true;
      callbackRef.current();
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0];
        if (entry?.isIntersecting && !hasFiredRef.current) {
          hasFiredRef.current = true;
          callbackRef.current();
          observer.disconnect();
        }
      },
      { threshold },
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [ref, threshold, disabled, signature]);
};
